import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/cn";
import { useOnlineStatus } from "./usePwaEnv";

/**
 * `ConnectivityBanner` — a slim, top-anchored online/offline banner. [task #15]
 *
 * While offline it stays visible ("you're offline — showing cached data"). On
 * reconnect it flashes a brief "back online" confirmation, then auto-dismisses.
 * The slide/fade entrance is wrapped in `motion-safe:`, so under
 * `prefers-reduced-motion` the banner simply appears/disappears with no motion.
 * RTL-correct via logical insets; Storybook-safe (online state settles in a
 * `useEffect`, so the static render shows nothing).
 */

const BACK_ONLINE_MS = 2600;

export function ConnectivityBanner() {
  const { t } = useTranslation();
  const online = useOnlineStatus();
  const [showBackOnline, setShowBackOnline] = useState(false);
  const wasOffline = useRef(false);

  useEffect(() => {
    if (!online) {
      wasOffline.current = true;
      setShowBackOnline(false);
      return;
    }
    // Only celebrate "back online" if we were actually offline before.
    if (wasOffline.current) {
      wasOffline.current = false;
      setShowBackOnline(true);
      const id = window.setTimeout(() => setShowBackOnline(false), BACK_ONLINE_MS);
      return () => window.clearTimeout(id);
    }
  }, [online]);

  if (online && !showBackOnline) return null;

  const offline = !online;

  return (
    <div
      data-testid="connectivity-banner"
      role="status"
      aria-live="polite"
      className={cn(
        "sticky top-0 z-[70] flex items-center justify-center gap-2 px-4 py-1.5 text-[12.5px] font-bold",
        "motion-safe:animate-[slidein_.25s_ease-out]",
        offline ? "bg-amber text-navy" : "bg-green text-white",
      )}
    >
      {offline ? <CloudOff /> : <CloudOn />}
      <span>{offline ? t("pwa.offlineTitle") : t("pwa.backOnline")}</span>
      {offline && <span className="font-medium opacity-80">— {t("pwa.offlineDesc")}</span>}
    </div>
  );
}

function CloudOff() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 2l20 20" />
      <path d="M5.8 6.2A6 6 0 0 0 8 18h10a4 4 0 0 0 1.5-7.7" />
      <path d="M9 6a6 6 0 0 1 9.6 3.6" />
    </svg>
  );
}

function CloudOn() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.5 19a4.5 4.5 0 0 0 .5-9 7 7 0 0 0-13.4 2A4 4 0 0 0 6 19z" />
      <path d="m9 13 2 2 4-4" />
    </svg>
  );
}
